import { useEffect, useRef, useCallback, useState } from 'react';

const speakers = [
  {
    name: 'Mariza Suares',
    talk: 'Cerimonialista e assessora especialista em casamentos',
    imageUrl: 'https://static.readdy.ai/image/e52027607460cffc32b49812e98784ba/93d99731700538251d41ae1a67600a06.jpeg',
  },
  {
    name: 'Gustavo Fause',
    talk: 'Chef especialista em alta gastronômia',
    imageUrl: 'https://static.readdy.ai/image/e52027607460cffc32b49812e98784ba/70d3e1abe1bcf73e0642b0c1481278a4.jpeg',
  },
  {
    name: 'Isabela Norbim',
    talk: 'Consultora de imagem para noivas',
    imageUrl: 'https://static.readdy.ai/image/e52027607460cffc32b49812e98784ba/394163e604421e45d02c329f278d6173.png',
  },
  {
    name: 'Andreza Minotto',
    talk: 'Maqueadora profissional',
    imageUrl: 'https://static.readdy.ai/image/e52027607460cffc32b49812e98784ba/e979515abbe8cfc33afe10ca6c9cc3a2.jpeg',
  },
];

export default function SpeakersSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateScrollButtons = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 10);
  }, []);

  const scrollBy = useCallback((direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.querySelector('.speaker-card')?.clientWidth || 280;
    const gap = 24;
    const scrollAmount = (cardWidth + gap) * 2;
    el.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
    setTimeout(updateScrollButtons, 400);
  }, [updateScrollButtons]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      updateScrollButtons();
      el.addEventListener('scroll', updateScrollButtons, { passive: true });
      window.addEventListener('resize', updateScrollButtons);
      return () => {
        el.removeEventListener('scroll', updateScrollButtons);
        window.removeEventListener('resize', updateScrollButtons);
      };
    }
  }, [updateScrollButtons]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-on-scroll').forEach((el) => {
              el.classList.add('revealed');
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="palestrantes" className="relative py-24 md:py-36" style={{ backgroundColor: 'oklch(var(--background-50))' }}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Section Header */}
        <div className="mb-16 md:mb-24 reveal-on-scroll">
          <span
            className="inline-block text-[10px] tracking-[0.3em] uppercase px-4 py-2 rounded-full border mb-6"
            style={{
              color: 'oklch(var(--secondary-500))',
              borderColor: 'oklch(var(--secondary-300) / 0.5)',
            }}
          >
            Palestrantes
          </span>
          <h2
            className="font-heading text-3xl md:text-5xl lg:text-6xl leading-[1.12]"
            style={{ color: 'oklch(var(--foreground-900))' }}
          >
            As vozes que vão
            <br />
            transformar sua visão
          </h2>
        </div>
      </div>

      {/* Carousel — Full width with overflow */}
      <div className="reveal-on-scroll relative px-6 md:px-10">
        {/* Left Arrow */}
        <button
          onClick={() => scrollBy('left')}
          className={`hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full items-center justify-center transition-all duration-300 ${
            canScrollLeft ? 'opacity-100 cursor-pointer' : 'opacity-0 pointer-events-none'
          }`}
          style={{
            backgroundColor: 'oklch(var(--background-50))',
            border: '1px solid oklch(var(--foreground-200) / 0.3)',
            color: 'oklch(var(--foreground-900))',
          }}
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>

        {/* Scrollable Track */}
        <div
          ref={scrollRef}
          className="flex gap-6 overflow-x-auto pb-4 speakers-scroll"
          style={{
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {speakers.map((speaker) => (
            <div
              key={speaker.name}
              className="speaker-card flex-shrink-0 flex flex-col rounded-xl overflow-hidden transition-all duration-300 hover:shadow-md cursor-pointer"
              style={{
                width: '280px',
                scrollSnapAlign: 'start',
                backgroundColor: 'oklch(var(--background-50))',
                border: '1px solid oklch(var(--background-200) / 0.4)',
              }}
            >
              {/* Speaker Photo — 3:4 ratio, clean */}
              <div className="relative overflow-hidden w-full" style={{ aspectRatio: '3/4' }}>
                <img
                  src={speaker.imageUrl}
                  alt={speaker.name}
                  title={`${speaker.name} - The Future Wedding Palestrante`}
                  className="w-full h-full object-cover transition-all duration-500 hover:scale-105"
                />
              </div>

              {/* Speaker Info — Clean */}
              <div className="p-5 text-center">
                <h3 className="font-heading text-base md:text-lg mb-1" style={{ color: 'oklch(var(--foreground-900))' }}>
                  {speaker.name}
                </h3>
                <p className="text-xs leading-relaxed" style={{ color: 'oklch(var(--foreground-500))' }}>
                  {speaker.talk}
                </p>
              </div>
            </div>
          ))}

          {/* "E muito mais" teaser card */}
          <div
            className="speaker-card flex-shrink-0 flex flex-col rounded-xl overflow-hidden transition-all duration-300 cursor-pointer"
            style={{
              width: '280px',
              scrollSnapAlign: 'start',
              backgroundColor: 'oklch(var(--secondary-100) / 0.5)',
              border: '1px dashed oklch(var(--secondary-300) / 0.6)',
            }}
          >
            <div
              className="flex flex-col items-center justify-center w-full flex-1"
              style={{ aspectRatio: '3/4' }}
            >
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mb-5"
                style={{ backgroundColor: 'oklch(var(--secondary-200) / 0.5)' }}
              >
                <i
                  className="ri-add-line text-2xl"
                  style={{ color: 'oklch(var(--secondary-500))' }}
                ></i>
              </div>
              <span
                className="font-heading text-xl"
                style={{ color: 'oklch(var(--foreground-900))' }}
              >
                e muito mais
              </span>
            </div>
          </div>
        </div>

        {/* Right Arrow */}
        <button
          onClick={() => scrollBy('right')}
          className={`hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full items-center justify-center transition-all duration-300 ${
            canScrollRight ? 'opacity-100 cursor-pointer' : 'opacity-0 pointer-events-none'
          }`}
          style={{
            backgroundColor: 'oklch(var(--background-50))',
            border: '1px solid oklch(var(--foreground-200) / 0.3)',
            color: 'oklch(var(--foreground-900))',
          }}
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>
      </div>

      {/* Mobile hint */}
      <div className="flex md:hidden justify-center mt-6 gap-1.5">
        {speakers.map((_, i) => (
          <div
            key={i}
            className="w-1.5 h-1.5 rounded-full"
            style={{ backgroundColor: 'oklch(var(--foreground-200))' }}
          ></div>
        ))}
        <span className="text-[10px] ml-2 tracking-wide" style={{ color: 'oklch(var(--foreground-400))' }}>
          Deslize para ver mais
        </span>
      </div>
    </section>
  );
}